import { z } from 'zod';
import { SourceRef } from './ask.js';
import { SynthesisSection } from './dossier.js';
/**
 * A share token that grants unauthenticated read access to a saved brief.
 * One brief can have multiple active tokens (e.g. separate links per
 * principal, per board meeting). Revocation is a soft-revoke stamp
 * (`revokedAt` / `revokedBy` below) — the doc is preserved so admins can
 * audit who shared what with whom and who pulled access, while public
 * reads treat revoked tokens as not found so `/b/:token` stops resolving.
 * Tokens are URL-safe random strings; the string itself is the Firestore
 * document ID, so a leaked link is sufficient to read the brief until
 * revoked (same trust model as a signed cloud-storage URL).
 */
export const BriefShareToken = z.object({
    token: z.string(),
    briefId: z.string(),
    createdBy: z.string(),
    createdByName: z.string().optional(),
    createdAt: z.string(),
    label: z.string().optional(),
    /**
     * Soft-revoke trail. When an analyst (or a cascade from brief-delete)
     * revokes a share link, we DO NOT hard-delete the doc — we stamp these
     * fields so admins retain a full history of who shared what, with whom,
     * and who pulled access. `readBriefShare` returns null for revoked docs
     * so the `/b/:token` surface stops resolving immediately.
     */
    revokedAt: z.string().optional(),
    revokedBy: z.string().optional(),
    revokedByName: z.string().optional(),
    /**
     * Principal-visibility telemetry. Stamped every time the unauth
     * `GET /api/briefs/shared/:token` endpoint serves the brief. `viewCount`
     * is a best-effort counter (no FieldValue.increment in the mock; plain
     * read-modify-write on the server), sufficient for showing the analyst
     * "this link was opened N times" in the share panel.
     */
    lastOpenedAt: z.string().optional(),
    viewCount: z.number().int().nonnegative().optional(),
});
export const CreateBriefShareRequest = z.object({
    label: z.string().max(120).optional(),
});
export const CreateBriefShareResponse = z.object({
    token: BriefShareToken,
    url: z.string(),
});
/**
 * Public projection of a brief served to unauthenticated clients hitting
 * `GET /api/briefs/shared/:token`. Strips the owner UID (not a principal's
 * concern) and returns only the snapshot fields plus the analyst's display
 * name. The server adds `sharedAt` at read time so the rendered artifact
 * can show both the original authored-at date and the moment the principal
 * opened it.
 */
export const PublicBrief = z.object({
    id: z.string(),
    entityName: z.string(),
    entityNameFa: z.string().optional(),
    classification: z.string(),
    headline: z.string(),
    sections: z.array(SynthesisSection).default([]),
    sources: z.array(SourceRef).default([]),
    authorName: z.string().optional(),
    synthesisModel: z.string().optional(),
    synthesisGeneratedAt: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    sharedAt: z.string(),
});
